import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MessageCircle, Package } from "lucide-react";
import { API, getConfig, inr, waLink } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AccountShell, { StatusPill, fmtDate, PAY_LABEL } from "@/components/AccountShell";

export default function MyOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const [wa, setWa] = useState("");

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/orders/mine`).then((r) => setOrders(r.data)).catch(() => setOrders([]));
    getConfig().then((c) => setWa(c.whatsapp_number || ""));
  }, [user]);

  return (
    <AccountShell title="My Orders" eyebrow={user ? `Hello, ${user.name.split(" ")[0]}` : "Account"} testId="my-orders-page">
      {orders === null ? (
        <p className="text-sm text-muted-foreground">Loading your orders…</p>
      ) : orders.length === 0 ? (
        <div className="max-w-md rounded-3xl border border-cream-muted bg-cream-secondary p-8" data-testid="orders-empty">
          <Package className="h-8 w-8 text-terracotta" />
          <p className="mt-3 font-display text-2xl">No orders yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Your future orders will show up here with their status.</p>
          <Link to="/#catalog" className="mt-5 inline-block rounded-full bg-terracotta px-5 py-2.5 text-sm font-semibold text-white" data-testid="orders-empty-shop-link">Start shopping</Link>
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2" data-testid="orders-list">
          {orders.map((o) => (
            <article key={o.order_id} className="rounded-3xl border border-cream-muted bg-cream-secondary p-6" data-testid={`order-card-${o.order_number}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-terracotta">{o.order_number}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{fmtDate(o.created_at)}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <StatusPill status={o.status} testId={`order-status-${o.order_number}`} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{PAY_LABEL[o.payment_status] || o.payment_status}</span>
                </div>
              </div>
              <ul className="mt-4 space-y-1.5 border-t border-cream-muted pt-4 text-sm">
                {o.items.map((i) => (
                  <li key={i.product_id} className="flex justify-between gap-3" data-testid={`account-order-item-${o.order_id}-${i.product_id}`}><span className="min-w-0 break-words">{i.name} <span className="text-muted-foreground">{i.unit} ×{i.quantity}</span></span><span className="shrink-0 font-semibold" data-testid={`account-order-line-total-${o.order_id}-${i.product_id}`}>{inr(i.line_total)}</span></li>
                ))}
              </ul>
              <div className="mt-4 flex items-center justify-between border-t border-cream-muted pt-3">
                <span className="font-semibold">Total</span>
                <span className="font-display text-xl font-bold text-terracotta">{inr(o.total)}</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to={`/order/${o.order_id}/tracking`} className="rounded-full border border-ragi/20 px-4 py-2 text-xs font-semibold text-ragi" data-testid={`order-track-${o.order_number}`}>Track delivery</Link>
                <Link to={`/order/${o.order_id}`} className={`rounded-full px-4 py-2 text-xs font-semibold ${o.payment_status === "pending" || o.payment_status === "failed" ? "bg-terracotta text-white" : "border border-ragi/20 text-ragi-dark hover:bg-cream"}`} data-testid={`order-view-${o.order_number}`}>{o.payment_status === "pending" || o.payment_status === "failed" ? "Pay now" : "View receipt"}</Link>
                {wa && (
                  <a href={waLink(wa, `Hi Kandula Home Foods! Checking on my order ${o.order_number}.`)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-4 py-2 text-xs font-bold text-white" data-testid={`order-whatsapp-${o.order_number}`}><MessageCircle className="h-3.5 w-3.5" /> Ask on WhatsApp</a>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </AccountShell>
  );
}
